import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowUpRight, MapPin, Briefcase, Clock, Banknote, GitBranch, Sparkles,
  Heart, ShieldCheck, Zap, Users, Globe, ArrowLeft, Send, Loader2, Check, ChevronRight,
} from "lucide-react";

/**
 * /careers — Connexis hiring page.
 *
 * Built to do two jobs at once: attract genuine candidates AND act as
 * credibility signal for prospective buyers who land on /customers, /about,
 * etc. ("These guys are hiring senior heads — they're a real operation.").
 *
 * Premium navy/slate aesthetic matching /start. Subtle motion.
 */
const ROLES = [
  {
    slug: "head-of-client-relations",
    title: "Head of Client Relations",
    team: "Revenue · Buyer success",
    location: "London / Hybrid",
    type: "Full-time",
    band: "£75–95k + equity",
    summary: "Own the post-signup buyer journey. Onboard launch partners, scale the playbook to 200+ accounts, and turn first-month NPS into 12-month retention.",
    fit: [
      "5+ years in B2B SaaS customer success or strategic account management",
      "Track record of taking customers from £5k MRR → £50k+ within 18 months",
      "Comfortable in the data — wallet metrics, lead conversion, churn cohorts",
      "Bias for direct, in-person relationships over support tickets",
    ],
    impact: [
      "Build the customer success playbook from zero",
      "Hire and lead 4–6 CSMs in the first 12 months",
      "Own the NRR target (we're targeting 130%)",
    ],
  },
  {
    slug: "head-of-marketing",
    title: "Head of Marketing",
    team: "Revenue · Acquisition",
    location: "London / Hybrid",
    type: "Full-time",
    band: "£80–110k + equity",
    summary: "Own demand generation, brand and content. We're a category-creator in the UK lead-routing space — your job is to make sure every serious acquisition team in finance hears about Connexis before they buy from anyone else.",
    fit: [
      "7+ years in B2B SaaS marketing, ideally in fintech or martech",
      "Have built a category brand from <£1m to >£10m ARR",
      "Editorial instincts — long-form content, podcasts, sharp positioning",
      "Hands-on with paid acquisition (LinkedIn, Google) AND organic",
    ],
    impact: [
      "Set and own the pipeline target",
      "Hire content + paid + brand specialists",
      "Make /docs, /customers, and /changelog the best in the category",
    ],
  },
  {
    slug: "senior-backend-engineer",
    title: "Senior Backend Engineer",
    team: "Engineering · Platform",
    location: "Remote (UK/EU)",
    type: "Full-time",
    band: "£90–130k + equity",
    summary: "Own the routing engine, billing infrastructure and webhook delivery. Python/FastAPI/MongoDB. We process every inbound lead through code you write — sub-100ms median latency, idempotent everything, audit-trail everything.",
    fit: [
      "6+ years building production Python services — async/await fluent",
      "Deep MongoDB experience including indexing, aggregation, transactions",
      "Have built billing or payments infra you're proud of",
      "Care about correctness — money flows through this code",
    ],
    impact: [
      "Own ping-tree v2 (multi-buyer parallel routing)",
      "Lead the CRM connector platform (HubSpot/Salesforce/Pipedrive)",
      "Set the engineering bar for the next 5 hires",
    ],
  },
  {
    slug: "compliance-officer",
    title: "Compliance & Risk Officer",
    team: "Operations · Risk",
    location: "London / Hybrid",
    type: "Full-time",
    band: "£65–85k + equity",
    summary: "Own GDPR, FCA CONC and TCPA posture as we scale into regulated financial sectors. You're the person prospective buyers ask the hardest questions to — and the one whose work makes them say yes.",
    fit: [
      "ICA / IRM qualified or working towards",
      "5+ years in fintech, lead-gen or consumer-finance compliance",
      "Comfortable owning DPIAs, sub-processor reviews, and audit responses",
      "Strong written communication — you'll write the trust posture customers read",
    ],
    impact: [
      "Lead our SOC 2 Type II readiness work in 2026",
      "Own the consent & audit trail product surface",
      "Brief sales on what we will and won't accept as a customer",
    ],
  },
  {
    slug: "senior-product-designer",
    title: "Senior Product Designer",
    team: "Engineering · Product",
    location: "Remote (UK/EU)",
    type: "Full-time",
    band: "£70–100k + equity",
    summary: "Make Connexis the best-looking enterprise routing platform on the planet. You'll work across marketing site, buyer dashboards, admin tools and the publisher portal — one designer, one taste, end-to-end.",
    fit: [
      "Portfolio that demonstrates Stripe / Linear / Vercel quality bar",
      "Comfortable in Figma + lightly hands-on in React/Tailwind",
      "Care about the gap between great and 'AI slop'",
      "Can ship a polished page end-to-end without needing a PM",
    ],
    impact: [
      "Own the design system end-to-end",
      "Ship one customer-facing flow every 2 weeks",
      "Set the visual bar that competitors copy",
    ],
  },
  {
    slug: "publisher-success-lead",
    title: "Publisher Success Lead",
    team: "Operations · Supply",
    location: "London / Hybrid",
    type: "Full-time",
    band: "£55–75k + commission",
    summary: "Own the supply side — the publishers who source leads onto Connexis. Recruit, vet, onboard, monitor quality and grow the volume that fills our buyer demand. Half acquisition, half ops, half compliance.",
    fit: [
      "3+ years in performance marketing, affiliate or lead-gen operations",
      "Network in UK affiliate / lead-gen space",
      "Numerate — you'll be reading approval rates and dispute trends daily",
      "Tough but fair — bad publishers stay bad and need to go",
    ],
    impact: [
      "Grow vetted publisher base from N to 100 in 12 months",
      "Own dispute resolution and quality scoring",
      "Recruit + manage 2–3 publisher account managers",
    ],
  },
];

const VALUES = [
  { Icon: ShieldCheck, label: "Boring on the basics",  body: "Money, compliance, uptime, security — we are deeply boring. You don't lose customers because the routing fabric had an opinion about a new framework." },
  { Icon: Zap,         label: "Ship narrow, ship sharp", body: "We don't ship features. We ship one polished thing every 2 weeks that earns its place. The roadmap is a knife, not a buffet." },
  { Icon: Heart,       label: "Treat money seriously",   body: "We move real cash for real customers. Every code path that touches a wallet is reviewed twice. We're paid to be careful — and we are." },
  { Icon: Users,       label: "Adults only",             body: "No bureaucracy, no theatre, no rituals for the sake of rituals. We pay senior people senior money and trust them to behave senior." },
];

const BENEFITS = [
  "Equity in a profitable, fast-growing fintech",
  "Salary at or above London market for your role",
  "Remote-friendly · London HQ when you want it",
  "£2,500 / year personal development budget",
  "Latest Apple kit + £1,500 home-office stipend",
  "33 days holiday + your birthday off + UK bank holidays",
  "Private health (Vitality) + mental health (Spill)",
  "Annual offsite — last one was 4 days in Lisbon",
];

export default function Careers() {
  const { slug } = useParams();
  if (slug) {
    const role = ROLES.find(r => r.slug === slug);
    if (role) return <RoleDetail role={role} />;
  }
  return <CareersIndex />;
}

function CareersIndex() {
  return (
    <div className="bg-white" data-testid="careers-page">

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950 text-white border-b border-slate-900">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_-10%,rgba(0,85,255,0.18),transparent_55%)] pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_110%,rgba(34,211,166,0.08),transparent_55%)] pointer-events-none" />
        <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(to_right,white_1px,transparent_1px),linear-gradient(to_bottom,white_1px,transparent_1px)] [background-size:64px_64px] pointer-events-none" />

        <div className="max-w-6xl mx-auto px-6 py-20 lg:py-28 relative">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-sm border border-white/10 bg-white/5 text-[10.5px] uppercase tracking-[0.22em] text-white/65 font-mono mb-8"
          >
            <Briefcase className="size-3" />
            We're hiring · 2026 cohort
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-4xl sm:text-6xl lg:text-7xl tracking-tight font-semibold leading-[0.98] max-w-4xl"
          >
            Help us build the<br />
            <span className="text-white/55">infrastructure of </span>
            <span className="bg-gradient-to-r from-white via-[#22D3A6] to-[#0055FF] bg-clip-text text-transparent">customer acquisition.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-7 text-[15.5px] text-white/65 max-w-2xl leading-relaxed"
          >
            Six senior roles. Six chances to own a category-defining piece of the Connexis platform. We pay senior money, ship narrow + sharp, and treat people like adults.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="mt-10 flex items-center gap-3 flex-wrap"
          >
            <a href="#roles">
              <Button className="rounded-full bg-white text-slate-950 hover:bg-white/90 font-semibold px-6 h-11 gap-2">
                See open roles <ArrowUpRight className="size-4" />
              </Button>
            </a>
            <Button asChild variant="outline" className="rounded-full border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-white h-11">
              <Link to="/about">About Connexis</Link>
            </Button>
          </motion.div>

          {/* Mini-stats */}
          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/5 border border-white/10 rounded-md overflow-hidden">
            {[
              { v: "6", l: "open roles" },
              { v: "£75k+", l: "median base" },
              { v: "0.1–1%", l: "equity range" },
              { v: "33+", l: "holiday days" },
            ].map((s) => (
              <div key={s.l} className="bg-slate-950/60 px-5 py-5">
                <div className="text-2xl sm:text-3xl font-semibold tracking-tight tabular-nums">{s.v}</div>
                <div className="text-[10.5px] uppercase tracking-[0.16em] text-white/45 font-mono mt-1.5">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm border border-slate-200 bg-slate-50 text-[10.5px] uppercase tracking-[0.22em] text-slate-500 font-mono mb-5">
            How we work
          </div>
          <h2 className="text-3xl sm:text-5xl tracking-tight font-semibold text-slate-900 max-w-3xl leading-tight">
            Four operating principles.<br />
            <span className="text-slate-500">Everything else negotiable.</span>
          </h2>
          <div className="mt-12 grid md:grid-cols-2 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
            {VALUES.map((v) => (
              <div key={v.label} className="bg-white p-7 sm:p-8">
                <span className="inline-flex size-9 items-center justify-center rounded-sm bg-slate-900 text-white">
                  <v.Icon className="size-4" strokeWidth={1.6} />
                </span>
                <h3 className="mt-5 text-lg font-semibold text-slate-900 tracking-tight">{v.label}</h3>
                <p className="mt-2 text-[14px] text-slate-600 leading-relaxed max-w-md">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles */}
      <section id="roles" className="py-20 lg:py-28 bg-slate-50/60 border-y border-slate-100 scroll-mt-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm border border-slate-200 bg-white text-[10.5px] uppercase tracking-[0.22em] text-slate-500 font-mono mb-5">
            Open positions · {ROLES.length} roles
          </div>
          <h2 className="text-3xl sm:text-5xl tracking-tight font-semibold text-slate-900 max-w-3xl leading-tight">
            Roles we're hiring for<br />
            <span className="text-slate-500">right now.</span>
          </h2>

          <div className="mt-12 space-y-3">
            {ROLES.map((r, i) => (
              <motion.div
                key={r.slug}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
              >
                <Link
                  to={`/careers/${r.slug}`}
                  className="block rounded-md border border-slate-200 bg-white p-6 hover:border-slate-300 hover:shadow-[0_2px_24px_-12px_rgba(15,23,42,0.18)] transition-all group"
                  data-testid={`role-card-${r.slug}`}
                >
                  <div className="flex items-start justify-between gap-6 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-mono">{r.team}</div>
                      <h3 className="mt-2 text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight group-hover:text-slate-950">
                        {r.title}
                      </h3>
                      <p className="mt-2.5 text-[14px] text-slate-600 leading-relaxed max-w-2xl">{r.summary}</p>
                      <div className="mt-4 flex items-center gap-4 text-[12px] text-slate-500 flex-wrap font-medium">
                        <span className="inline-flex items-center gap-1.5"><MapPin className="size-3" /> {r.location}</span>
                        <span className="text-slate-300">·</span>
                        <span className="inline-flex items-center gap-1.5"><Clock className="size-3" /> {r.type}</span>
                        <span className="text-slate-300">·</span>
                        <span className="inline-flex items-center gap-1.5"><Banknote className="size-3" /> {r.band}</span>
                      </div>
                    </div>
                    <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-900 group-hover:translate-x-1 transition-transform">
                      View role <ChevronRight className="size-4" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm border border-slate-200 bg-slate-50 text-[10.5px] uppercase tracking-[0.22em] text-slate-500 font-mono mb-5">
            What you get
          </div>
          <h2 className="text-3xl sm:text-5xl tracking-tight font-semibold text-slate-900 max-w-3xl leading-tight">
            Pay senior people senior money.<br />
            <span className="text-slate-500">Then get out of their way.</span>
          </h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
            {BENEFITS.map((b, i) => (
              <div key={i} className="bg-white p-5 flex gap-3 items-start">
                <Check className="size-4 mt-0.5 text-emerald-600 shrink-0" strokeWidth={2.5} />
                <span className="text-[14px] text-slate-700 leading-relaxed">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-slate-950 text-white relative overflow-hidden border-t border-slate-900">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(0,85,255,0.18),transparent_55%)] pointer-events-none" />
        <div className="max-w-3xl mx-auto px-6 text-center relative">
          <h2 className="text-3xl sm:text-5xl tracking-tight font-semibold leading-tight">
            Don't see your role?
          </h2>
          <p className="mt-5 text-[15px] text-white/60 max-w-xl mx-auto leading-relaxed">
            If you'd be a category-defining hire and the listed roles aren't quite right, email us anyway. We'd rather hire the right person than fill the right job description.
          </p>
          <div className="mt-9">
            <Button asChild className="rounded-full bg-white text-slate-950 hover:bg-white/90 font-semibold px-6 h-11 gap-2">
              <a href="mailto:kieran@connex.is" data-testid="careers-email-cta">
                kieran@connex.is <ArrowUpRight className="size-4" />
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

function RoleDetail({ role }) {
  const [form, setForm] = useState({ name: "", email: "", linkedin: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return; // re-entry guard — defends against double-clicks / accidental double-fire
    if (!form.name?.trim() || !form.email?.trim() || !form.message?.trim()) {
      toast.error("Name, email and message are required.");
      return;
    }
    if (form.message.trim().length < 20) {
      toast.error("Tell us a bit more — at least 20 characters in the message.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/careers/apply", {
        ...form,
        role_slug:  role.slug,
        role_title: role.title,
      });
      setSubmitted(true);
      toast.success("Application received — we read every one.");
    } catch (err) {
      // Catches network errors, validation errors, and any unexpected throw.
      // We deliberately do NOT rethrow — keeps the form interactive.
      // eslint-disable-next-line no-console
      console.error("[careers/apply] submit failed", err);
      const msg = err?.response?.data?.detail || err?.message || "Could not submit — try emailing kieran@connex.is";
      toast.error(typeof msg === "string" ? msg : "Could not submit — try emailing kieran@connex.is");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white min-h-screen" data-testid="role-detail">
      <section className="bg-slate-950 text-white border-b border-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_-10%,rgba(0,85,255,0.18),transparent_55%)] pointer-events-none" />
        <div className="max-w-4xl mx-auto px-6 py-16 lg:py-20 relative">
          <Link to="/careers" className="inline-flex items-center gap-1.5 text-[12.5px] text-white/55 hover:text-white mb-6 font-medium">
            <ArrowLeft className="size-3.5" /> All roles
          </Link>
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-white/55 font-mono">{role.team}</div>
          <h1 className="mt-3 text-3xl sm:text-5xl tracking-tight font-semibold leading-tight">{role.title}</h1>
          <div className="mt-5 flex items-center gap-4 text-[13px] text-white/60 flex-wrap font-medium">
            <span className="inline-flex items-center gap-1.5"><MapPin className="size-3.5" /> {role.location}</span>
            <span className="text-white/20">·</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="size-3.5" /> {role.type}</span>
            <span className="text-white/20">·</span>
            <span className="inline-flex items-center gap-1.5"><Banknote className="size-3.5" /> {role.band}</span>
          </div>
          <p className="mt-7 text-[15.5px] text-white/75 leading-relaxed max-w-2xl">{role.summary}</p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-16 grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-3 space-y-10">
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Who you are</h2>
            <ul className="mt-5 space-y-3.5">
              {role.fit.map((f, i) => (
                <li key={i} className="flex gap-3 text-[14.5px] text-slate-700 leading-relaxed">
                  <Check className="size-4 mt-0.5 text-slate-900 shrink-0" strokeWidth={2.5} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">What you'll own</h2>
            <ul className="mt-5 space-y-3.5">
              {role.impact.map((f, i) => (
                <li key={i} className="flex gap-3 text-[14.5px] text-slate-700 leading-relaxed">
                  <Sparkles className="size-4 mt-0.5 text-[#0055FF] shrink-0" strokeWidth={2} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <aside className="lg:col-span-2">
          <div className="rounded-md border border-slate-200 bg-white p-6 lg:sticky lg:top-24">
            {submitted ? (
              <div className="text-center py-6" data-testid="application-submitted">
                <div className="inline-flex size-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 mb-4">
                  <Check className="size-6" strokeWidth={2.2} />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 tracking-tight">Application received</h3>
                <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">We read every application. You'll hear from us within 5 working days.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3.5" data-testid="application-form">
                <h3 className="text-base font-semibold text-slate-900 tracking-tight">Apply for this role</h3>
                <Input placeholder="Full name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="apply-name" />
                <Input type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="apply-email" />
                <Input placeholder="LinkedIn or portfolio URL" value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} data-testid="apply-linkedin" />
                <Textarea placeholder="Why you? *" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="apply-message" />
                <Button type="submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="apply-submit">
                  {busy ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                  Submit application
                </Button>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Or email <a href={`mailto:kieran@connex.is?subject=${encodeURIComponent(role.title)}`} className="underline text-slate-700">kieran@connex.is</a>
                </p>
              </form>
            )}
          </div>
        </aside>
      </section>
    </div>
  );
}
